import React, { useMemo, useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Box, Typography, Button, CircularProgress, Chip } from '@mui/material';
import { message } from 'antd';
import axios from 'axios';
import './PreScreeningModal.css';

const PreviewCVModal = ({ open, setOpen, record, backendURL, getCookie, title = "CV Preview", showAnonymizeButton = true }) => {
    const [processing, setProcessing] = useState(false);
    const [showButton, setShowButton] = useState(true);

    const closeModal = useCallback(() => {
        setOpen(false);
    }, [setOpen]);

    // Clean up the object URL when the modal is closed
    useEffect(() => {
        if (!open && record?.cvContent && record.cvContent.startsWith('blob:')) {
            URL.revokeObjectURL(record.cvContent);
        }
    }, [open, record?.cvContent]);

    // Handle anonymized CV email request
    const handleAnonymizeCV = useCallback(() => {
        if (!record?.ApplicationID) return;

        setProcessing(true);

        // Find the jobID either from the record or from the current URL
        const jobID = record?.JobID || new URLSearchParams(window.location.search).get('jobID') || '';

        const anonymizeURL = `${backendURL}/v1/anonymize-cv?sessionID=${getCookie("userSession")}&applicationID=${record.ApplicationID}&jobID=${jobID}`;

        axios.get(anonymizeURL)
            .then(response => {
                // Handle the 202 Accepted response with JSON data
                if (response.status === 202) {
                    const data = response.data;
                    // Extract recipient email if present in message
                    const emailMatch = data.message.match(/sent to ([^\s]+@[^\s]+)/);
                    const emailText = emailMatch ? ` (${emailMatch[1]})` : '';

                    // Show success message using antd message
                    message.success({
                        content: `${data.message || 'Your anonymized CV will be sent via email shortly.'}`,
                        duration: 8,
                        style: {
                            marginTop: '20px'
                        }
                    });

                } else {
                    // Handle unexpected response
                    console.warn("Unexpected response type:", response);
                    message.success({
                        content: 'Request accepted. Check your email for the anonymized CV.',
                        duration: 6
                    });
                }
                setProcessing(false);
            })
            .catch(error => {
                console.error("CV anonymization request failed: ", error);
                setProcessing(false);

                // Display appropriate error message based on response
                let errorMsg = 'Failed to process anonymized CV request. Please try again later.';

                if (error.response) {
                    switch (error.response.status) {
                        case 401:
                            errorMsg = 'Authentication error. Please log in again.';
                            break;
                        case 400:
                            errorMsg = 'Invalid request. Please try again with valid parameters.';
                            break;
                    }
                } else if (error.request) {
                    errorMsg = 'Network error. Please check your connection and try again.';
                }

                message.error({
                    content: errorMsg,
                    duration: 6
                });
            });
    }, [record, backendURL, getCookie]);

    // Modal style definitions
    const modalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '90%',
        maxWidth: '1200px',
        height: '85vh',
        bgcolor: '#f0f0f0',
        borderRadius: 2,
        boxShadow: 3,
        p: 4,
        display: 'flex',
        flexDirection: 'column',
    };

    // Function to render CV content based on the type of the document
    const renderCVContent = useMemo(() => {
        if (!record?.cvContent) return <Typography>Loading CV...</Typography>;

        if (['application/pdf', 'text/plain'].includes(record.cvType)) {
            const baseUrl = record.cvContent.includes('#') 
                ? record.cvContent.split('#')[0] 
                : record.cvContent;
            const pdfUrl = baseUrl + '#navpanes=0';
            return (
                <iframe
                    src={pdfUrl}
                    title="CV Preview"
                    style={{ width: '100%', height: '100%', border: 'none' }}
                />
            );
        }
        if (record.cvType === 'text/html') {
            return (
                <Box
                    dangerouslySetInnerHTML={{ __html: record.cvContent }}
                    sx={{ width: '100%', height: '100%', overflowY: 'auto' }}
                />
            );
        }
        return <Typography>Unsupported file type for preview.</Typography>;
    }, [record?.cvContent, record?.cvType]);

    return (
        <Modal
            open={open}
            onClose={closeModal}
            destroyOnClose={true}
            aria-labelledby="modal-title"
            BackdropProps={{
                style: {
                    backdropFilter: 'blur(3px)',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                },
            }}
        >
            <Box sx={modalStyle}>
                <Box display="flex" alignItems="center" mb={2}>
                    <Typography id="modal-title" variant="h5" component="h2" sx={{ color: 'primary.dark', fontWeight: 600 }}>
                        {title}
                    </Typography>
                </Box>
                <Box flexGrow={1} overflow="hidden" bgcolor="#ffffff" borderRadius={2} p={3}>
                    {renderCVContent}
                </Box>
                <Box width="100%" display="flex" justifyContent="space-between" mt={2}>
                    {showButton && showAnonymizeButton && (
                        <Box sx={{ position: 'relative' }}>
                            <Button
                                onClick={handleAnonymizeCV}
                                disabled={processing}
                                sx={{
                                    bgcolor: '#0A7CD3',
                                    color: '#fff',
                                    textTransform: 'none',
                                    px: 3,
                                    py: 1,
                                    borderRadius: 1,
                                    '&:hover': {
                                        bgcolor: '#0967a9',
                                    },
                                }}
                                startIcon={processing ? <CircularProgress size={20} color="inherit" /> : null}
                            >
                                {processing ? "Processing..." : "Email Anonymized CV"}
                            </Button>
                            <Chip
                                label="BETA"
                                size="small"
                                color="warning"
                                sx={{
                                    position: 'absolute',
                                    top: -10,
                                    right: -5,
                                    fontSize: '0.65rem',
                                    fontWeight: 'bold',
                                    height: '18px',
                                }}
                            />
                        </Box>
                    )}
                    <Button
                        onClick={closeModal}
                        sx={{
                            bgcolor: 'error.main',
                            color: '#fff',
                            textTransform: 'none',
                            px: 3,
                            py: 1,
                            borderRadius: 1,
                            '&:hover': {
                                bgcolor: 'error.dark',
                            },
                        }}
                    >
                        Close
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};

PreviewCVModal.propTypes = {
    open: PropTypes.bool.isRequired,
    setOpen: PropTypes.func.isRequired,
    record: PropTypes.shape({
        ApplicationID: PropTypes.string,
        cvContent: PropTypes.string,
        cvType: PropTypes.string,
        Name: PropTypes.string,
        JobID: PropTypes.string,
    }),
    backendURL: PropTypes.string.isRequired,
    getCookie: PropTypes.func.isRequired,
    title: PropTypes.string,
    showAnonymizeButton: PropTypes.bool,
};

export default PreviewCVModal;